// ** Redux Imports
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

// ** Axios Imports
import api from "@src/api/config"
import SuccessToast from "../../../components/common/toast/SuccessToast"
import ErrorToast from "../../../components/common/toast/ErrorToast"
import { toast } from "react-toastify"
import { ToastSuccess } from "../../../assets/images/icons"
import { showModalError } from "../../../redux/modalTypeError"
import { showModalSuccess } from "../../../redux/modalTypeSuccess"
import fileDownload from "js-file-download"
// ** AsyncThunk Imports
import { downloadAllProfiles } from "./download-all-profiles"

const normalizeFolio = (value = "") =>  value
    ?.toString()
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")

const getRowFolios = (row) =>  [
    row?.quotation_folio,
    row?.folio,
    row?.quotation_number,
    row?.quotation_code
  ].filter(Boolean)

const rowMatchesFolio = (row, expected) => {
  const rowFolios = getRowFolios(row)
  return rowFolios.some((folioValue) => {
    const rowFolio = normalizeFolio(folioValue)
    return (
      rowFolio === expected ||
      rowFolio.endsWith(expected) ||
      expected.endsWith(rowFolio)
    )
  })
}

const hasFolioMatch = (response, folio) => {
  const rows = response?.data?.data
  if (!Array.isArray(rows) || !folio) return false
  const expected = normalizeFolio(folio)
  if (!expected) return false
  return rows.some((row) => rowMatchesFolio(row, expected))
}

const keepMatchingFolios = (response, folio) => {
  const rows = response?.data?.data
  if (!Array.isArray(rows) || !folio) return response
  const expected = normalizeFolio(folio)
  const filteredRows = rows.filter((row) => rowMatchesFolio(row, expected))

  if (filteredRows.length === 0) return response

  return {
    ...response,
    data: {
      ...response.data,
      data: filteredRows,
      meta: {
        ...response?.data?.meta,
        total: filteredRows.length,
        from: filteredRows.length > 0 ? 1 : null,
        to: filteredRows.length > 0 ? filteredRows.length : null,
        current_page: 1,
        last_page: 1
      }
    }
  }
}

const buildFolioVariants = (folio) => {
  const raw = folio?.toString().trim()
  if (!raw) return []
  const withoutHyphen = raw.replace(/-/g, "")
  const suffix = raw.includes("-") ? raw.split("-").pop()?.trim() : ""
  const noPrefix = raw.replace(/^[A-Za-z]+-?/, "")
  return Array.from(
    new Set([raw, suffix, noPrefix, withoutHyphen].filter(Boolean))
  )
}

const fetchQuotationsWithFolioFallback = async (url, requestParams) => {
  const response = await api.get(url, { params: requestParams })
  const isFolioSearch = requestParams?.type === "folio" && !!requestParams?.folio
  if (!isFolioSearch) {
    return response
  }

  const targetFolio = requestParams.folio
  if (hasFolioMatch(response, targetFolio)) {
    return keepMatchingFolios(response, targetFolio)
  }

  const sanitizeParams = (params) => {
    const cleanParams = { ...params }
    Object.keys(cleanParams).forEach((key) => {
      if (cleanParams[key] === undefined || cleanParams[key] === null) {
        delete cleanParams[key]
      }
    })
    return cleanParams
  }

  const seen = new Set()
  const addAttempt = (attempts, attemptParams) => {
    const cleanAttempt = sanitizeParams(attemptParams)
    const key = JSON.stringify(cleanAttempt)
    if (!seen.has(key)) {
      seen.add(key)
      attempts.push(cleanAttempt)
    }
  }

  const attempts = []
  const noBusinessLine = { ...requestParams }
  delete noBusinessLine.business_line

  const folioVariants = buildFolioVariants(targetFolio)
  folioVariants.forEach((folioValue) => {
    addAttempt(attempts, { ...noBusinessLine, type: "folio", folio: folioValue })
    addAttempt(attempts, {
      ...noBusinessLine,
      type: "quotation_folio",
      quotation_folio: folioValue,
      folio: undefined
    })
    addAttempt(attempts, {
      ...noBusinessLine,
      type: "folio",
      folio: folioValue,
      quotation_folio: folioValue
    })
  })

  for (const retryParams of attempts) {
    const retryResponse = await api.get(url, { params: retryParams })
    if (hasFolioMatch(retryResponse, targetFolio)) {
      return keepMatchingFolios(retryResponse, targetFolio)
    }

    const retryRows = retryResponse?.data?.data
    if (Array.isArray(retryRows) && retryRows.length === 1) {
      return retryResponse
    }
  }

  return response
}

export const getQuotations = createAsyncThunk(
  "quotations/get",
  async (params, { getState }) => {
    const state = getState()
    const { productSelected } = state.products
    const requestParams = {
      ...params,
      product: productSelected?.value
    }
    const businessLines = state.businessLines?.businessLineSelected?.id
    requestParams.business_line = businessLines
    const response = await fetchQuotationsWithFolioFallback(
      `/api/quotations?created=internal`,
      requestParams
    )
    return {
      data: response.data,
      params: requestParams
    }
  }
)

export const getExternalQuotations = createAsyncThunk(
  "quotations/getExternalQuotations",
  async (params, { getState }) => {
    try {
      const state = getState()
      const { productSelected } = state.products
      const requestParams = {
        ...params,
        product: productSelected?.value
      }
      const businessLines = state.businessLines?.businessLineSelected?.id
      requestParams.business_line = businessLines
      const response = await fetchQuotationsWithFolioFallback(
        `/api/quotations?created=external`,
        requestParams
      )
      return {
        data: response.data,
        params: requestParams
      }
    } catch (error) {
      console.error("Error fetching external quotations:", error)
      return {
        data: null,
        params
      }
    }
  }
)


export const getMyQuotations = createAsyncThunk(
  "quotations/getMyQuotations",
  async (params, { getState }) => {
    const state = getState()
    const { productSelected } = state.products
    const requestParams = {
      ...params,
      product: productSelected?.value
    }
    const businessLines = state.businessLines?.businessLineSelected?.id
    requestParams.business_line = businessLines
    const response = await fetchQuotationsWithFolioFallback(
      `/api/quotations`,
      requestParams
    )
    return {
      data: response.data
    }
  }
)

export const getQuotationDetails = createAsyncThunk(
  "quotations/details",
  async (id) => {
    const response = await api.get(`api/quotations/${id}/details`)
    return {
      data: response.data
    }
  }
)

/**
 * @description funcion para mandar y modificar el client_id de la cotización
 */
export const updateQuotationClient = createAsyncThunk("quotations/updateClient", async (params) => {
  const response = await api.put(`api/quotations/updateClient/${params.id}`, {
    client_id: params.client_id
  })
  return {
    data: response.data
  }
})

/**
 * @description funcion para actualizar la cotización y marcar que ya se convirtió en certificado
 * @param {number} id
 */
export const convertQuoteToCertificate = createAsyncThunk("quotations/convertToClient", async (id) => {
  const response = await api.put(`api/quotations/convertToClient/${id}`)
  return {
    data: response.data
  }
})

export const getQuotationsOptions = createAsyncThunk(
  "quotations/getQuotationsOptions",
  async (quotationId) => {
    const response = await api.get(`/api/quotations/${quotationId}/options`)
    return {
      data: response.data
    }
  }
)

export const saveQuotation = createAsyncThunk(
  "quotations/post",
  /**
   * @param {any} newQuotationData - Data for the new quotation.
   * @param {import('@reduxjs/toolkit').ThunkAPI} thunkAPI
   */
  async (newQuotationData, { dispatch, getState, rejectWithValue }) => {
    const state = getState()
    const { productSelected } = state.products
    const businessLines = state.businessLines?.businessLineSelected?.id

    const apiPayload = {
      ...newQuotationData,
      product: productSelected?.value,
      business_line: businessLines
    }
    try {
      const response = await api.post(`/api/quotations`, apiPayload)
      dispatch(getQuotationsOptions(response.data.id))
      dispatch(getQuotations({ page: 1, product: productSelected?.value }))
      return {
        data: response.data,
        params: apiPayload
      }
    } catch (error) {
      console.error("message error", error.response.data)
      const message = error.response.data.message
      // redirection
      const state = getState()
      const { userData } = state.auth
      let url = ""
      if (userData.role === "admin") {
        url = "/admin/quotations"
      } else if (userData.role === "seller") {
        url = "/seller/quotations"
      }
      dispatch(
        showModalError({
          message,
          url
        })
      )
      return rejectWithValue(error.response?.data || { message: "Failed to save quotation" })
    }
  }
)

export const loadQuotationsMassive = createAsyncThunk(
  "quotations/massive",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`api/quotations/massive`, data)

      return {
        data: response.data
      }
    } catch (error) {
      dispatch(
        showModalError({
          message: "Lo sentimos, hay un error en la información del archivo."
        })
      )

      return rejectWithValue(error.response.data)
    }
  }
)

export const saveQuotationsMassive = createAsyncThunk(
  "quotations/massive/save",
  async (data, { dispatch, rejectWithValue, getState }) => {
    try {
      const response = await api.post(`api/quotations/massive/save`, data)
      const state = getState()
      const { userData } = state.auth

      let url = "/admin/quotations"
      if (userData?.role === "seller") {
        url = "/seller/quotations"
      }

      if (response?.data?.status === "success") {
        dispatch(
          showModalSuccess({
            message: response.data.message || "Las cotizaciones se importaron correctamente.",
            url
          })
        )
      }

      return {
        data: response.data
      }
    } catch (error) {
      dispatch(
        showModalError({
          message:
            "Lo sentimos, hay un error al guardar las cotizaciones de carga masiva."
        })
      )

      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const getSuggestions = createAsyncThunk(
  "commissions/suggestions",
  async (params, { getState }) => {
    const state = getState()
    const { productSelected } = state.products
    params.product = productSelected?.value
    const businessLines = state.businessLines?.businessLineSelected?.id
    params.business_line = businessLines
    const response = await api.get(`api/quotations/suggestions`, {
      params
    })
    return {
      data: response.data,
      params
    }
  }
)

export const getExternalSuggestions = createAsyncThunk(
  "commissions/externalSuggestions",
  async (params, { getState }) => {
    const state = getState()
    const { productSelected } = state.products
    params.product = productSelected?.value
    const businessLines = state.businessLines?.businessLineSelected?.id
    params.business_line = businessLines
    const response = await api.get(
      `api/quotations/suggestions?created=external`,
      {
        params
      }
    )
    return {
      data: response.data,
      params
    }
  }
)

export const getMySuggestions = createAsyncThunk(
  "commissions/mySuggestions",
  async (params, { getState }) => {
    const state = getState()
    const { productSelected } = state.products
    params.product = productSelected?.value
    const businessLines = state.businessLines?.businessLineSelected?.id
    params.business_line = businessLines
    const response = await api.get(`api/quotations/suggestions`, {
      params
    })
    return {
      data: response.data,
      params
    }
  }
)

export const sendQuotationByEmail = createAsyncThunk(
  "commissions/sendQuotationByEmail",
  async ({ quotation_id, params }) => {
    try {
      const response = await api.post(`api/quotations/${quotation_id}/email`, {
        ...params
      })
      toast(<SuccessToast message={response.data.message} />, {
        icon: <ToastSuccess />,
        hideProgressBar: true,
        className: "toast-success",
        autoClose: 2000
      })
      return {
        data: response.data,
        params
      }
    } catch (err) {
      toast(<ErrorToast message={"Error al enviar el correo"} />, {
        icon: null,
        hideProgressBar: true,
        className: "toast-danger",
        autoClose: 4000
      })
      throw err
    }
  }
)

// TODO move to different store, maybe this should not be in quotations section
export const getClientFee = createAsyncThunk(
  "clients/fee",
  async ({ client, params }) => {
    try {
      const response = await api.get(`api/clients/fees/${client}`, {
        params
      })
      return {
        data: response.data,
        params
      }
    } catch (err) {
      console.error(err)
    }
  }
)

/**
 * @description una funcion para mandar el id de una cotización y descargar todos los perfiles de los proveedores recibidos por un array mandados al api para recibirlos  en un zip y descargarlo
 * @param {number} quotationId
 * @param {number[]} providersId
 * @returns {Promise}
 */
export const downloadProfilesAll = createAsyncThunk(
  "quotations/downloadProfilesAll",
  async (params) => {
    try {
      const response = await api.get(`/api/quotations/providers/${params.quotationId}`, {
        params: {
          providersid: params.providersid
        },
        responseType: 'blob'
      })
      if (response) {
        if (params.providersid.length > 1) {
          fileDownload(response.data, "cotizaciones.zip")
        }
        toast(<SuccessToast message={"Descarga exitosa"} />, {
          icon: <ToastSuccess />,
          hideProgressBar: true,
          className: "toast-success",
          autoClose: 2000
        })
        return {
          blob: response.data,
          filename: "cotizaciones.zip"
        }
      }
    } catch (err) {
      console.log("❌ | index.js:261 | err:", err)
    }
  }
)

const clearResponse = createAsyncThunk("commissions/clearResponse", () => null)

/**
 * @typedef {Object} QuotationState
 * @property {any} data
 * @property {any} dataExternal
 * @property {any} myQuotations
 * @property {any} details
 * @property {string} suggestions.keyFilter
 * @property {string[]} suggestions.folio
 * @property {string[]} suggestions.seller
 * @property {string[]} suggestions.client
 * @property {any} tempQuotation
 * @property {string} externalSuggestions.keyFilter
 * @property {string[]} externalSuggestions.folio
 * @property {string[]} externalSuggestions.seller
 * @property {string[]} externalSuggestions.client
 * @property {string} mySuggestions.keyFilter
 * @property {string[]} mySuggestions.folio
 * @property {string[]} mySuggestions.seller
 * @property {string[]} mySuggestions.client
 * @property {any[]} quotationsOptions
 * @property {any} response
 * @property {any} summaryData
 * @property {any} client
 * @property {any} summaryData
 * @property {any} client
 * @property {any} salesFee
 * @property {string} name
 * @property {string} email
 * @property {any} clientFees
 * @property {'idle' | 'loading' | 'success' | 'error'} emailSendingStatus  - Status of email sending async thunk
 * @property {'idle' | 'loading' | 'success' | 'error'} downloadAllProfilesStatus
 * @property {Blob | null} downloadAllProfilesResponse
 * @property {String | null} downloadAllProfilesFilename
 */

/**
 * @type {QuotationState}
 */
const initialState = {
  data: null,
  dataExternal: null,
  myQuotations: null,
  details: null,
  suggestions: {
    keyFilter: "seller",
    folio: [],
    seller: [],
    client: []
  },
  tempQuotation: null,
  externalSuggestions: {
    keyFilter: "seller",
    folio: [],
    seller: [],
    client: []
  },
  mySuggestions: {
    keyFilter: "client",
    folio: [],
    seller: [],
    client: []
  },
  quotationsOptions: [],
  response: null,
  summaryData: null,
  // client and sales fee selected to quotation
  client: null,
  salesFee: null,
  cliente_final: null,
  name: "",
  email: "",
  clientFees: null,
  emailSendingStatus: "idle",
  status: "idle",
  downloadAllProfilesStatus: "idle",
  downloadAllProfilesResponse: null,
  downloadAllProfilesFilename: null
}

export const quotationsSlice = createSlice({
  name: "quotations",
  initialState,
  reducers: {
    reset: () => initialState,
    saveSummaryData: (state, action) => {
      state.summaryData = action.payload
    },
    updateQuotationSelected: (state, action) => {
      state.client = action.payload.client
      state.salesFee = action.payload.salesFee
      state.cliente_final = action.payload.cliente_final ?? null
      state.name = action.payload.name
      state.email = action.payload.email
      state.prospectId = action.payload.prospectId
    },
    setKeyFilter: (state, action) => {
      state.suggestions.keyFilter = action.payload
    },
    setMyQuotationsKeyFilter: (state, action) => {
      state.mySuggestions.keyFilter = action.payload
    },
    setExternalQuotationsKeyFilter: (state, action) => {
      state.externalSuggestions.keyFilter = action.payload
    },
    clearEmailStatus: (state) => {
      state.emailSendingStatus = "idle"
    },
    clearDownloadAllProfilesStatus: (state) => {
      state.downloadAllProfilesResponse = null
      state.downloadAllProfilesStatus = "idle"
      state.downloadAllProfilesFilename = null
    }
  },
  extraReducers: (builder) => {
    builder.addCase(saveQuotation.fulfilled, (state, action) => {
      if (action.payload?.data) {
        state.tempQuotation = action.payload.data
      }
    })
    builder.addCase(loadQuotationsMassive.fulfilled, (state, action) => {
      state.status = "success"
      if (action.payload?.data) {
        state.tempQuotation = action.payload.data
      }
    })
    builder.addCase(loadQuotationsMassive.pending, (state) => {
      state.status = "loading"
    })
    builder.addCase(loadQuotationsMassive.rejected, (state, action) => {
      state.status = "error"
      state.response = {
        message: action.payload || action.error.message
      }
    })
    builder.addCase(saveQuotationsMassive.pending, (state) => {
      state.status = "loading"
    })
    builder.addCase(saveQuotationsMassive.fulfilled, (state, action) => {
      state.status = "success"
      state.response = action.payload?.data || null
    })
    builder.addCase(saveQuotationsMassive.rejected, (state, action) => {
      state.status = "error"
      state.response = {
        message: action.payload || action.error.message
      }
    })
    builder.addCase(getQuotations.fulfilled, (state, action) => {
      state.data = action.payload.data
      state.suggestions = {
        folio: action.payload?.data?.meta?.suggestions,
        seller: action.payload?.data?.meta?.seller_suggestions,
        client: action.payload?.data?.meta?.client_suggestions,
        keyFilter: state.suggestions.keyFilter
      }
    })
    builder.addCase(getExternalQuotations.fulfilled, (state, action) => {
      state.dataExternal = action.payload.data
      state.externalSuggestions = {
        folio: action.payload?.data?.meta?.suggestions,
        seller: action.payload?.data?.meta?.seller_suggestions,
        client: action.payload?.data?.meta?.client_suggestions,
        keyFilter: state.externalSuggestions.keyFilter
      }
    })
    builder.addCase(getMyQuotations.fulfilled, (state, action) => {
      state.myQuotations = action.payload.data
      state.mySuggestions = {
        folio: action.payload?.data?.meta?.suggestions,
        seller: action.payload?.data?.meta?.seller_suggestions,
        client: action.payload?.data?.meta?.client_suggestions,
        keyFilter: state.mySuggestions.keyFilter
      }
    })
    builder.addCase(getQuotationDetails.fulfilled, (state, action) => {
      state.details = action.payload.data
    })
    builder.addCase(getSuggestions.fulfilled, (state, action) => {
      state.suggestions = action.payload.data
    })
    builder.addCase(getClientFee.fulfilled, (state, action) => {
      state.clientFees = action.payload?.data
    })
    builder.addCase(getExternalSuggestions.fulfilled, (state, action) => {
      state.externalSuggestions = action.payload.data
    })
    builder.addCase(getMySuggestions.fulfilled, (state, action) => {
      state.mySuggestions = action.payload.data
    })
    builder.addCase(getQuotationsOptions.fulfilled, (state, action) => {
      state.quotationsOptions = action.payload.data
    })
    builder.addCase(sendQuotationByEmail.pending, (state) => {
      state.emailSendingStatus = "loading"
    })
    builder.addCase(sendQuotationByEmail.fulfilled, (state, action) => {
      state.response = action.payload.data
      state.emailSendingStatus = "success"
    })
    builder.addCase(sendQuotationByEmail.rejected, (state) => {
      state.response = null
      state.emailSendingStatus = "error"
    })
    builder.addCase(clearResponse.fulfilled, (state) => {
      state.response = null
    })
    builder.addCase(downloadAllProfiles.pending, (state) => {
      state.downloadAllProfilesStatus = "loading"
    })
    builder.addCase(downloadAllProfiles.fulfilled, (state, action) => {
      state.downloadAllProfilesStatus = "success"
      state.downloadAllProfilesResponse = action.payload.blob
      state.downloadAllProfilesFilename = action.payload.filename
    })
  }
})

export const {
  clearEmailStatus,
  reset,
  saveSummaryData,
  setExternalQuotationsKeyFilter,
  setKeyFilter,
  setMyQuotationsKeyFilter,
  updateQuotationSelected,
  clearDownloadAllProfilesStatus
} = quotationsSlice.actions
export * from "./download-all-profiles"
export default quotationsSlice.reducer
